import { Component, type ReactNode } from "react";
import type Rollbar from "rollbar";

import { t } from "i18n/i18n";
import { SquareButton } from "mds/components/buttons/SquareButton";

interface LevelErrorType extends Error {
  level: "warning" | "error";
}

interface Props {
  children: ReactNode;
  minimal?: boolean;
}

interface State {
  hasError: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: LevelErrorType) {
    if (error) {
      console.error("Error: ", error);
    }
    const method = error.level === "warning" ? "warning" : "error";
    if (window.Rollbar && window.Rollbar[method]) {
      window.Rollbar[method](error as Rollbar.LogArgument);
    }
  }

  render() {
    const { hasError } = this.state;
    const { children, minimal } = this.props;
    if (hasError) {
      if (minimal) {
        return (
          <div className="flex h-full w-full items-center justify-center" role="alert">
            <div>{t("worksheet.error.boundary_minimal")}</div>
          </div>
        );
      }
      return (
        <div className="mx-auto my-0 w-[500px]" role="alert">
          <h1 className="h1">{t("error.error")}</h1>
          <div className="body">{t("worksheet.error.boundary_message")}</div>
          <div className="mt-4 flex justify-around">
            <SquareButton kind="accent" size="m" onClick={() => window.location.reload()}>
              {t("worksheet.error.try_again")}
            </SquareButton>
          </div>
        </div>
      );
    }

    return children;
  }
}
